import { Card, DatePicker, Spin, Table, Tabs, Tag } from "antd";
import type { TableColumnsType } from "antd";
import { customerAnalyticsApi } from "api/customerAnalytics.api";
import { AppSelect } from "components/antd-ui";
import { customerStageOptions, getRejectReasonLabel } from "config/customerStage";
import { fetchStaffOptions, useRemoteOptions } from "hooks/useRemoteOptions";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { userStore } from "store/userStore";
import { CustomerStage } from "types/customer";
import { getTitle } from "utils";
import { can } from "utils/auth";
import dayjs, { Dayjs } from "utils/dayjs";
import { customerIndustryOptions, customerSourceOptions, getCustomerSourceLabel } from "views/Customer/customer.meta";

const CUSTOMER_PATH = "/customer";

type StageCounts = Record<CustomerStage, number>;

interface Group {
  key: string;
  label: string;
  total: number;
  stages: StageCounts;
}

interface AnalyticsData {
  summary: { total: number; stages: StageCounts };
  rejectReasons: { code: string; label: string; total: number }[];
  byStaff: Group[];
  bySource: Group[];
  byIndustry: Group[];
  byPartner: Group[];
  byMonth: Group[];
}

const EMPTY_STAGES: StageCounts = { NEW: 0, INTERESTED: 0, QUOTED: 0, SOLD: 0, REJECTED: 0 };
const EMPTY_DATA: AnalyticsData = {
  summary: { total: 0, stages: EMPTY_STAGES },
  rejectReasons: [],
  byStaff: [],
  bySource: [],
  byIndustry: [],
  byPartner: [],
  byMonth: [],
};

const percent = (part: number, whole: number) => (whole > 0 ? `${((part / whole) * 100).toFixed(1)}%` : "--");

// Bảng phân tích theo 1 chiều (sale, nguồn, ngành, đối tác, tháng); drillParam = tên tham số lọc ở trang Khách hàng (nếu có)
const GroupTable = ({
  groups,
  labelTitle,
  drillParam,
  formatLabel,
}: {
  groups: Group[];
  labelTitle: string;
  drillParam?: "staffId" | "industry" | "partnerId";
  formatLabel?: (group: Group) => string;
}) => {
  const link = (group: Group, stage?: CustomerStage) => {
    if (!drillParam || group.key === "0" || group.key === "") return null;
    const params = new URLSearchParams({ [drillParam]: group.key });
    if (stage) params.set("stage", stage);
    return `/customer?${params.toString()}`;
  };

  const countCell = (group: Group, value: number, stage?: CustomerStage) => {
    const to = link(group, stage);
    return to && value > 0 ? <Link to={to}>{value}</Link> : <span>{value}</span>;
  };

  const columns: TableColumnsType<Group> = [
    {
      title: labelTitle,
      key: "label",
      render: (_, group) => <span className="font-semibold">{formatLabel ? formatLabel(group) : group.label || "--"}</span>,
    },
    {
      title: "Tổng khách",
      key: "total",
      width: 110,
      align: "right",
      render: (_, group) => countCell(group, group.total),
    },
    ...customerStageOptions.map((stage) => ({
      title: stage.label,
      key: stage.value,
      width: 130,
      align: "right" as const,
      render: (_: unknown, group: Group) => countCell(group, group.stages[stage.value] || 0, stage.value),
    })),
    {
      title: "Tỷ lệ chốt",
      key: "rate",
      width: 110,
      align: "right",
      render: (_, group) => percent(group.stages.SOLD, group.total),
    },
  ];

  return (
    <Table<Group>
      rowKey="key"
      size="small"
      pagination={false}
      dataSource={groups}
      columns={columns}
      locale={{ emptyText: "Chưa có dữ liệu" }}
      scroll={{ x: 900 }}
    />
  );
};

export const CustomerAnalyticsPage = observer(({ title = "" }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<AnalyticsData>(EMPTY_DATA);
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [staffId, setStaffId] = useState<number>();
  const [source, setSource] = useState<string>();
  const [industry, setIndustry] = useState<string>();

  const onlyMine = !userStore.info.role?.isAdmin && can(CUSTOMER_PATH, "ONLY_MINE");
  const staffFilter = useRemoteOptions(fetchStaffOptions, !onlyMine);

  useEffect(() => {
    document.title = getTitle(title);
  }, [title]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await customerAnalyticsApi.findAll({
        from: range ? range[0].startOf("day").unix() : undefined,
        to: range ? range[1].endOf("day").unix() : undefined,
        staffId,
        source,
        industry,
      });
      setData(res.data || EMPTY_DATA);
    } finally {
      setLoading(false);
    }
  }, [industry, range, source, staffId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const { total, stages } = data.summary;
  const reached = useMemo(
    () => ({
      interested: stages.INTERESTED + stages.QUOTED + stages.SOLD,
      quoted: stages.QUOTED + stages.SOLD,
      sold: stages.SOLD,
    }),
    [stages],
  );
  const maxReason = Math.max(1, ...data.rejectReasons.map((reason) => reason.total));

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <div className="flex flex-wrap items-center gap-3">
          <DatePicker.RangePicker
            value={range}
            onChange={(value) => setRange(value && value[0] && value[1] ? [value[0], value[1]] : null)}
            format="DD/MM/YYYY"
            placeholder={["Khách tạo từ", "đến ngày"]}
            presets={[
              { label: "Tháng này", value: [dayjs().startOf("month"), dayjs().endOf("month")] },
              { label: "30 ngày qua", value: [dayjs().subtract(29, "day"), dayjs()] },
              { label: "Năm nay", value: [dayjs().startOf("year"), dayjs().endOf("year")] },
            ]}
          />
          {!onlyMine && (
            <AppSelect
              style={{ width: 200 }}
              allowClear
              showSearch
              filterOption={false}
              placeholder="Sale chăm sóc"
              value={staffId}
              options={staffFilter.options}
              onSearch={staffFilter.onSearch}
              onChange={(value) => {
                const next = typeof value === "number" ? value : undefined;
                staffFilter.select(next);
                setStaffId(next);
              }}
            />
          )}
          <AppSelect
            style={{ width: 170 }}
            allowClear
            placeholder="Nguồn"
            value={source}
            options={customerSourceOptions}
            onChange={(value) => setSource(value ? String(value) : undefined)}
          />
          <AppSelect
            style={{ width: 190 }}
            allowClear
            placeholder="Ngành nghề"
            value={industry}
            options={customerIndustryOptions}
            onChange={(value) => setIndustry(value ? String(value) : undefined)}
          />
        </div>
      </Card>

      <Spin spinning={loading}>
        <div className="flex flex-col gap-4">
          <Card title={`Phễu khách hàng (${total} khách)`}>
            <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))" }}>
              {customerStageOptions.map((stage) => (
                <div key={stage.value} className="rounded-lg border border-gray-200 p-4">
                  <Tag color={stage.color}>{stage.label}</Tag>
                  <div className="text-2xl font-bold mt-2">
                    <Link to={`/customer?stage=${stage.value}`}>{stages[stage.value]}</Link>
                  </div>
                  <div className="text-xs text-gray-500">{percent(stages[stage.value], total)} tổng khách</div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-8 mt-4 font-semibold">
              <span>Khách → Có quan tâm: {percent(reached.interested, total)}</span>
              <span>Có quan tâm → Gửi báo giá: {percent(reached.quoted, reached.interested)}</span>
              <span>Gửi báo giá → Đã bán: {percent(reached.sold, reached.quoted)}</span>
              <span>Khách → Đã bán: {percent(reached.sold, total)}</span>
            </div>
          </Card>

          <Card title="Lý do từ chối">
            {data.rejectReasons.length ? (
              <div className="flex flex-col gap-2">
                {data.rejectReasons.map((reason) => (
                  <div key={reason.code} className="flex items-center gap-3">
                    <div style={{ width: 200 }}>{getRejectReasonLabel(reason.code)}</div>
                    <div style={{ flex: 1, background: "#f2f4f7", borderRadius: 4, height: 18 }}>
                      <div
                        style={{
                          width: `${(reason.total / maxReason) * 100}%`,
                          background: "#f04438",
                          borderRadius: 4,
                          height: 18,
                        }}
                      />
                    </div>
                    <div style={{ width: 90 }} className="text-right font-semibold">
                      {reason.total} ({percent(reason.total, stages.REJECTED)})
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-gray-400">Chưa có khách từ chối</div>
            )}
          </Card>

          <Card title="Phân tích theo nhóm khách">
            <Tabs
              items={[
                {
                  key: "staff",
                  label: "Sale chăm sóc",
                  children: <GroupTable groups={data.byStaff} labelTitle="Sale" drillParam="staffId" />,
                },
                {
                  key: "source",
                  label: "Nguồn khách",
                  children: (
                    <GroupTable
                      groups={data.bySource}
                      labelTitle="Nguồn"
                      formatLabel={(group) => getCustomerSourceLabel(group.label) || "Chưa có nguồn"}
                    />
                  ),
                },
                {
                  key: "industry",
                  label: "Ngành nghề",
                  children: (
                    <GroupTable
                      groups={data.byIndustry}
                      labelTitle="Ngành nghề"
                      drillParam="industry"
                      formatLabel={(group) => group.label || "Chưa có ngành"}
                    />
                  ),
                },
                {
                  key: "partner",
                  label: "Đối tác giới thiệu",
                  children: <GroupTable groups={data.byPartner} labelTitle="Đối tác" drillParam="partnerId" />,
                },
                {
                  key: "month",
                  label: "Tháng khách được tạo",
                  children: (
                    <GroupTable
                      groups={data.byMonth}
                      labelTitle="Tháng"
                      formatLabel={(group) => dayjs(`${group.key}-01`).format("MM/YYYY")}
                    />
                  ),
                },
              ]}
            />
          </Card>
        </div>
      </Spin>
    </div>
  );
});
